import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import {
  InventoryStoreAccessSection,
  type StoreYardOption,
} from "../components/console-users/InventoryStoreAccessSection";
import { PermissionModulePicker } from "../components/console-users/PermissionModulePicker";
import { CONSOLE_PERMISSION_LABELS } from "../config/consolePermissionLabels";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { permRecordNeedsInventoryStoreChoice } from "../utils/inventoryStoreScope";

type RowUser = {
  id: string;
  email: string;
  displayName: string;
  role: "superadmin" | "admin";
  permissions: string[];
  inventoryStoreYardIds?: string[] | null;
  createdAt?: string | null;
};

function AddConsoleUserForm({
  keys,
  labelsById,
  yardOptions,
  yardsLoading,
  currentUserIsSuperadmin,
  onCreated,
}: {
  keys: string[];
  labelsById: Map<string, string>;
  yardOptions: StoreYardOption[];
  yardsLoading: boolean;
  currentUserIsSuperadmin: boolean;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "superadmin">("admin");
  const [newPermRecord, setNewPermRecord] = useState<Record<string, boolean>>({});
  const [newInvAllStores, setNewInvAllStores] = useState(true);
  const [newInvSelectedIds, setNewInvSelectedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUserIsSuperadmin && role === "superadmin") setRole("admin");
  }, [currentUserIsSuperadmin, role]);

  return (
    <form
      className="milestone-master-modal-editor"
      onSubmit={(e) => {
        e.preventDefault();
        setFormErr(null);
        setBusy(true);
        void (async () => {
          try {
            const merged = { ...Object.fromEntries(keys.map((k) => [k, false])), ...newPermRecord };
            const perms = role === "superadmin" ? [] : keys.filter((k) => merged[k]);
            if (
              role === "admin" &&
              permRecordNeedsInventoryStoreChoice(merged) &&
              !newInvAllStores &&
              newInvSelectedIds.length === 0
            ) {
              throw new Error("Inventory store access: choose “All stores” or select at least one store / yard.");
            }
            const base = getApiBaseUrl();
            const res = await apiFetch(`${base}/api/console-users`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: email.trim(),
                displayName: displayName.trim(),
                password,
                role,
                permissions: perms,
                inventoryStoreYardIds:
                  role === "superadmin" || !permRecordNeedsInventoryStoreChoice(merged)
                    ? undefined
                    : newInvAllStores
                      ? null
                      : newInvSelectedIds,
              }),
            });
            const j = (await res.json().catch(() => ({}))) as { error?: string };
            if (!res.ok) throw new Error(j.error || res.statusText);
            setEmail("");
            setDisplayName("");
            setPassword("");
            setRole("admin");
            setNewPermRecord({});
            setNewInvAllStores(true);
            setNewInvSelectedIds([]);
            onCreated();
          } catch (ex) {
            setFormErr(ex instanceof Error ? ex.message : String(ex));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      {formErr ? (
        <p className="alert alert--danger" role="alert" style={{ marginBottom: "0.75rem" }}>
          {formErr}
        </p>
      ) : null}

      <h3 className="form-section-title">Account</h3>
      <div className="field-grid">
        <div className="field">
          <label htmlFor="cu-email">Email</label>
          <input id="cu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="cu-name">Display name</label>
          <input id="cu-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cu-pass">Password (min 8)</label>
          <input
            id="cu-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>
        <div className="field">
          <label htmlFor="cu-role">Role</label>
          <select id="cu-role" value={role} onChange={(e) => setRole(e.target.value as "admin" | "superadmin")}>
            <option value="admin">Admin (scoped permissions)</option>
            {currentUserIsSuperadmin ? <option value="superadmin">Superadmin (full access)</option> : null}
          </select>
        </div>
      </div>

      {role === "admin" ? (
        <>
          <h3 className="form-section-title">Module access</h3>
          <fieldset className="console-users-page__perms" aria-label="Module permissions">
            <PermissionModulePicker keys={keys} labelsById={labelsById} value={newPermRecord} onChange={setNewPermRecord} />
            <InventoryStoreAccessSection
              permRecord={{ ...Object.fromEntries(keys.map((k) => [k, false])), ...newPermRecord }}
              yards={yardOptions}
              yardsLoading={yardsLoading}
              allStores={newInvAllStores}
              selectedIds={newInvSelectedIds}
              onChange={({ allStores, selectedIds }) => {
                setNewInvAllStores(allStores);
                setNewInvSelectedIds(selectedIds);
              }}
            />
          </fieldset>
        </>
      ) : null}

      <div className="toolbar-row" style={{ marginTop: "0.75rem" }}>
        <button type="submit" className="btn btn--primary btn--tg" disabled={busy}>
          {busy ? "Saving…" : "Create user"}
        </button>
      </div>
    </form>
  );
}

function ConsoleUserSummaryCard({
  row,
  isActive,
  onOpen,
}: {
  row: RowUser;
  isActive: boolean;
  onOpen: () => void;
}) {
  const accessLine =
    row.role === "superadmin"
      ? "Full access · all modules"
      : `${row.permissions.length} permission${row.permissions.length === 1 ? "" : "s"}`;
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">
            {row.role === "superadmin" ? "Superadmin" : "Admin"}
          </span>
        </span>
        <span className="milestone-master-picker-card__title">{row.displayName || "—"}</span>
        <span className="milestone-master-picker-card__stats mono" aria-label="Email">
          <span>{row.email}</span>
        </span>
        <span className="milestone-master-picker-card__stats" aria-label="Permissions">
          <span>{accessLine}</span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click to manage user"}</span>
      </span>
    </button>
  );
}

function UserManagePanel({
  row,
  keys,
  labelsById,
  yardOptions,
  yardsLoading,
  onSaved,
  onClose,
  currentUserId,
  currentUserIsSuperadmin,
}: {
  row: RowUser;
  keys: string[];
  labelsById: Map<string, string>;
  yardOptions: StoreYardOption[];
  yardsLoading: boolean;
  onSaved: () => void;
  onClose: () => void;
  currentUserId: string;
  currentUserIsSuperadmin: boolean;
}) {
  const [np, setNp] = useState("");
  const [permRecord, setPermRecord] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(keys.map((k) => [k, row.permissions.includes(k)])),
  );
  const [invAllStores, setInvAllStores] = useState(() => row.inventoryStoreYardIds == null);
  const [invSelectedIds, setInvSelectedIds] = useState<string[]>(() => row.inventoryStoreYardIds ?? []);
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    setPermRecord(Object.fromEntries(keys.map((k) => [k, row.permissions.includes(k)])));
    setInvAllStores(row.inventoryStoreYardIds == null);
    setInvSelectedIds(row.inventoryStoreYardIds ?? []);
    setLocalErr(null);
    setNp("");
  }, [row.id, row.permissions, row.inventoryStoreYardIds, keys]);

  const savePatch = async (body: Record<string, unknown>): Promise<boolean> => {
    setBusy(true);
    setLocalErr(null);
    try {
      const base = getApiBaseUrl();
      const res = await apiFetch(`${base}/api/console-users/${encodeURIComponent(row.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(j.error || res.statusText);
      onSaved();
      return true;
    } catch (e) {
      setLocalErr(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const buildPermissionsPatch = (): Record<string, unknown> | null => {
    if (permRecordNeedsInventoryStoreChoice(permRecord) && !invAllStores && invSelectedIds.length === 0) {
      setLocalErr("Inventory store access: choose “All stores” or select at least one store / yard.");
      return null;
    }
    return {
      permissions: keys.filter((k) => permRecord[k]),
      inventoryStoreYardIds: permRecordNeedsInventoryStoreChoice(permRecord)
        ? invAllStores
          ? null
          : invSelectedIds
        : undefined,
    };
  };

  const savePermissions = async (): Promise<boolean> => {
    const body = buildPermissionsPatch();
    if (!body) return false;
    return savePatch(body);
  };

  const canDeleteRow =
    row.id !== currentUserId && (currentUserIsSuperadmin || row.role !== "superadmin");

  const deleteUser = async () => {
    if (!canDeleteRow) return;
    const ok = window.confirm(
      `Remove console user “${row.displayName || row.email}”?\n\nThey will no longer be able to sign in. This cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    setLocalErr(null);
    try {
      const base = getApiBaseUrl();
      const res = await apiFetch(`${base}/api/console-users/${encodeURIComponent(row.id)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || res.statusText);
      }
      onSaved();
      onClose();
    } catch (e) {
      setLocalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="milestone-master-modal-editor console-users-manage-panel">
      <div className="console-users-manage-panel__identity">
        <div className="console-users-page__user-line">
          <span className="table-name">{row.displayName || "—"}</span>
          <span className="console-users-page__user-sep" aria-hidden="true">
            ·
          </span>
          <span className="mono text-muted console-users-page__user-email-inline">{row.email}</span>
        </div>
        <div className="console-users-page__role-line">
          <span
            className={
              "console-users-page__role-pill" +
              (row.role === "superadmin" ? " console-users-page__role-pill--super" : "")
            }
          >
            {row.role === "superadmin" ? "Superadmin" : "Admin"}
          </span>
        </div>
      </div>

      {localErr ? (
        <p className="alert alert--danger" role="alert" style={{ margin: "0.75rem 0" }}>
          {localErr}
        </p>
      ) : null}

      {row.role === "superadmin" ? (
        <p className="text-muted section-lead section-lead--tight" style={{ marginTop: "0.5rem" }}>
          Superadmins always have full module access. You can change their password below.
        </p>
      ) : (
        <>
          <h3 className="form-section-title" style={{ marginTop: "0.75rem" }}>
            Module access
          </h3>
          <fieldset className="console-users-page__perms" aria-label="Module permissions">
            <PermissionModulePicker keys={keys} labelsById={labelsById} value={permRecord} onChange={setPermRecord} />
            <InventoryStoreAccessSection
              permRecord={permRecord}
              yards={yardOptions}
              yardsLoading={yardsLoading}
              allStores={invAllStores}
              selectedIds={invSelectedIds}
              onChange={({ allStores, selectedIds }) => {
                setInvAllStores(allStores);
                setInvSelectedIds(selectedIds);
              }}
            />
          </fieldset>
          <div className="toolbar-row" style={{ marginTop: "0.65rem" }}>
            <button
              type="button"
              className="btn btn--primary btn--tg btn--sm"
              disabled={busy}
              onClick={() => void savePermissions()}
            >
              {busy ? "Saving…" : "Update permissions"}
            </button>
          </div>
        </>
      )}

      <h3 className="form-section-title" style={{ marginTop: "1.25rem" }}>
        Password
      </h3>
      <div className="console-users-page__pw-cell" style={{ maxWidth: "22rem" }}>
        <label className="console-users-page__pw-label" htmlFor={`cu-pw-${row.id}`}>
          New password
        </label>
        <input
          id={`cu-pw-${row.id}`}
          type="password"
          placeholder="Min. 8 characters"
          value={np}
          onChange={(e) => setNp(e.target.value)}
        />
        <button
          type="button"
          className="btn btn--tg-secondary btn--sm"
          disabled={busy || np.length < 8}
          onClick={() => {
            void savePatch({ newPassword: np }).then((ok) => {
              if (ok) setNp("");
            });
          }}
        >
          Set password
        </button>
      </div>

      <div className="console-users-manage-panel__danger" style={{ marginTop: "1.25rem" }}>
        <h3 className="form-section-title">Danger zone</h3>
        {canDeleteRow ? (
          <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={() => void deleteUser()}>
            Delete user
          </button>
        ) : (
          <p className="text-muted section-lead section-lead--tight" style={{ margin: 0 }}>
            {row.id === currentUserId ? "You cannot delete the account you are signed in as." : "Only a superadmin may remove another superadmin."}
          </p>
        )}
      </div>
    </div>
  );
}

export function ConsoleUsersPage() {
  const { user, authRequired, can } = useAuth();
  const [rows, setRows] = useState<RowUser[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [manageUserId, setManageUserId] = useState<string | null>(null);

  const [yardOptions, setYardOptions] = useState<StoreYardOption[]>([]);
  const [yardsLoading, setYardsLoading] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    const base = getApiBaseUrl();
    const cfg = await fetch(`${base}/api/auth/config`).then((r) => r.json() as Promise<{ permissionKeys?: string[] }>);
    setKeys(Array.isArray(cfg.permissionKeys) ? cfg.permissionKeys : []);

    const res = await apiFetch(`${base}/api/console-users`);
    if (res.status === 404) {
      setErr("Console user API is only available when server has CONSOLE_AUTH_REQUIRED=true.");
      setRows([]);
      return;
    }
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      setErr(j.error || `Failed to load (${res.status})`);
      setRows([]);
      return;
    }
    setRows((await res.json()) as RowUser[]);
  }, []);

  const loadYardOptions = useCallback(async () => {
    const base = getApiBaseUrl();
    setYardsLoading(true);
    try {
      const res = await apiFetch(`${base}/api/console-users/store-yard-options`);
      if (!res.ok) {
        setYardOptions([]);
        return;
      }
      const j = (await res.json()) as StoreYardOption[];
      setYardOptions(Array.isArray(j) ? j : []);
    } catch {
      setYardOptions([]);
    } finally {
      setYardsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && can("console_users")) void load();
  }, [user, can, load]);

  useEffect(() => {
    if (user && can("console_users")) void loadYardOptions();
  }, [user, can, loadYardOptions]);

  const labelsById = useMemo(() => new Map(CONSOLE_PERMISSION_LABELS.map((x) => [x.id, x.label])), []);

  const manageRow = useMemo(
    () => (manageUserId ? rows.find((r) => r.id === manageUserId) : undefined),
    [manageUserId, rows],
  );

  useEffect(() => {
    if (manageUserId && !manageRow) setManageUserId(null);
  }, [manageUserId, manageRow]);

  const closeCreateModal = useCallback(() => setCreateModalOpen(false), []);

  useEffect(() => {
    if (!createModalOpen && !manageUserId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setManageUserId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createModalOpen, manageUserId, closeCreateModal]);

  if (!authRequired) {
    return (
      <div className="milestone-master-page">
        <PageHeader
          breadcrumb={[{ label: "Home", to: "/" }, { label: "Console users" }]}
          title="Console users"
          subtitle="HO console accounts are disabled until server authentication is turned on."
        />
        <p className="tab-intro">
          Enable <code>CONSOLE_AUTH_REQUIRED=true</code> on the server to manage HO accounts.
        </p>
      </div>
    );
  }

  if (user && !can("console_users")) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="milestone-master-page">
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Console users" }]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Console users
            <span className="milestone-master__catalog-count">{rows.length}</span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={() => setCreateModalOpen(true)}>
            Add user
          </button>
        }
      />

      {err ? (
        <p className="alert alert--danger" role="alert" style={{ marginBottom: "1rem" }}>
          {err}
        </p>
      ) : null}

      <section className="milestone-master__catalog" aria-labelledby="console-users-catalog-heading" style={{ marginTop: "1.75rem" }}>

        {rows.length === 0 ? (
          <div className="milestone-master__empty">
            <p className="milestone-master__empty-title">No console users yet</p>
            <p className="milestone-master__empty-desc">
              Use <strong>Add user</strong> to create the first admin or superadmin for this environment.
            </p>
          </div>
        ) : (
          <div className="milestone-master-picker-grid">
            {rows.map((r) => (
              <ConsoleUserSummaryCard
                key={r.id}
                row={r}
                isActive={manageUserId === r.id}
                onOpen={() => {
                  setCreateModalOpen(false);
                  setManageUserId((prev) => (prev === r.id ? null : r.id));
                }}
              />
            ))}
          </div>
        )}
      </section>

      {createModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            closeCreateModal();
          }}
        >
          <div
            className="modal milestone-master-modal console-users-manage-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="console-users-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="console-users-create-title" className="milestone-master-modal__title">
                Add console user
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <AddConsoleUserForm
                keys={keys}
                labelsById={labelsById}
                yardOptions={yardOptions}
                yardsLoading={yardsLoading}
                currentUserIsSuperadmin={user?.role === "superadmin"}
                onCreated={async () => {
                  closeCreateModal();
                  await load();
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {manageRow ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setManageUserId(null);
          }}
        >
          <div
            className="modal milestone-master-modal console-users-manage-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="console-users-manage-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="console-users-manage-title" className="milestone-master-modal__title">
                Manage console user
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setManageUserId(null)}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <UserManagePanel
                key={manageRow.id}
                row={manageRow}
                keys={keys}
                labelsById={labelsById}
                yardOptions={yardOptions}
                yardsLoading={yardsLoading}
                onSaved={() => void load()}
                onClose={() => setManageUserId(null)}
                currentUserId={user?.id ?? ""}
                currentUserIsSuperadmin={user?.role === "superadmin"}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
